import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import logo from '../assets/images/atadaboard-logo.png';
import { APP_LINKS } from '../data/site';

const NAV_ITEMS = [
  { label: 'Platform', id: 'platform' },
  { label: 'Features', id: 'features' },
  { label: 'Pricing', id: 'pricing' },
  { label: 'Stories', id: 'video-testimonials' },
];

const LOGIN_OPTIONS = [
  { name: 'Web console', detail: 'console.atadaboard.com', url: APP_LINKS.web },
  { name: 'iPhone and iPad', detail: 'Download on the App Store', url: APP_LINKS.ios },
  { name: 'Android', detail: 'Get it on Google Play', url: APP_LINKS.android },
];

const Header = () => {
  const [isCompact, setIsCompact] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const sentinelRef = useRef(null);
  const loginRef = useRef(null);

  /*
    The compact state is driven by a zero-height sentinel sitting at the top of
    the document rather than by a scroll listener, so nothing runs on the
    scroll path.
  */
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsCompact(!entry.isIntersecting),
      { threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (loginRef.current && !loginRef.current.contains(event.target)) {
        setIsLoginOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsLoginOpen(false);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  /* The drawer covers the page on small screens, so lock the body behind it. */
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMenuOpen]);

  const goTo = (sectionId) => {
    setIsMenuOpen(false);
    setIsLoginOpen(false);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" className="ab-header-sentinel" />

      <header className={`ab-header${isCompact ? ' is-compact' : ''}`}>
        <div className="ab-header__inner">
          <a
            href="/"
            className="ab-header__brand"
            aria-label="AtadaBoard home"
            onClick={(event) => {
              if (window.location.pathname === '/') {
                event.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img src={logo} alt="AtadaBoard school management software" width="2437" height="732" />
          </a>

          <nav className="ab-header__nav" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <button key={item.id} type="button" className="ab-header__link" onClick={() => goTo(item.id)}>
                {item.label}
              </button>
            ))}
          </nav>

          <div className="ab-header__actions">
            <div className="ab-header__login" ref={loginRef}>
              <button
                type="button"
                className="ab-header__loginBtn"
                onClick={() => setIsLoginOpen((open) => !open)}
                aria-expanded={isLoginOpen}
                aria-haspopup="true"
              >
                Log in
                <ChevronDown size={15} strokeWidth={1.75} className={isLoginOpen ? 'is-open' : ''} />
              </button>

              {isLoginOpen && (
                <div className="ab-header__menu" role="menu">
                  {LOGIN_OPTIONS.map((option) => (
                    <a
                      key={option.name}
                      href={option.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ab-header__menuItem"
                      role="menuitem"
                      onClick={() => setIsLoginOpen(false)}
                    >
                      <span>
                        <strong>{option.name}</strong>
                        <em>{option.detail}</em>
                      </span>
                      <ArrowUpRight size={16} strokeWidth={1.75} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            <button type="button" className="ab-btn ab-btn--primary ab-btn--sm" onClick={() => goTo('demo-form')}>
              Book a demo
            </button>
          </div>

          <button
            type="button"
            className="ab-header__burger"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X size={22} strokeWidth={1.75} /> : <Menu size={22} strokeWidth={1.75} />}
          </button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="ab-drawer">
          <nav className="ab-drawer__nav" aria-label="Mobile">
            {NAV_ITEMS.map((item) => (
              <button key={item.id} type="button" className="ab-drawer__link" onClick={() => goTo(item.id)}>
                {item.label}
              </button>
            ))}
          </nav>

          <div className="ab-drawer__foot">
            <button
              type="button"
              className="ab-btn ab-btn--primary ab-btn--block"
              onClick={() => goTo('demo-form')}
            >
              Book a demo
            </button>

            <p className="ab-drawer__legend">Log in to AtadaBoard</p>
            <div className="ab-drawer__apps">
              {LOGIN_OPTIONS.map((option) => (
                <a
                  key={option.name}
                  href={option.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ab-drawer__app"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {option.name}
                  <ArrowUpRight size={15} strokeWidth={1.75} />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .ab-header-sentinel {
          position: absolute;
          top: 0;
          height: 1px;
          width: 100%;
          pointer-events: none;
        }

        .ab-header {
          position: sticky;
          top: 0;
          z-index: var(--ab-z-header);
          background: rgba(255, 255, 255, 0.86);
          backdrop-filter: saturate(180%) blur(14px);
          -webkit-backdrop-filter: saturate(180%) blur(14px);
          border-bottom: 1px solid transparent;
          transition: border-color 0.25s var(--ab-ease), box-shadow 0.25s var(--ab-ease),
            background-color 0.25s var(--ab-ease);
        }

        /* Blur is decorative; keep the bar legible without it. */
        @media (prefers-reduced-transparency: reduce) {
          .ab-header {
            background: var(--ab-paper);
            backdrop-filter: none;
            -webkit-backdrop-filter: none;
          }
        }

        .ab-header.is-compact {
          background: rgba(255, 255, 255, 0.94);
          border-bottom-color: var(--ab-line);
          box-shadow: 0 8px 24px -20px rgba(6, 41, 68, 0.5);
        }

        .ab-header__inner {
          max-width: var(--ab-container);
          margin: 0 auto;
          padding-inline: var(--ab-gutter);
          height: 76px;
          display: flex;
          align-items: center;
          gap: 2rem;
          transition: height 0.25s var(--ab-ease);
        }

        .ab-header.is-compact .ab-header__inner { height: 62px; }

        .ab-header__brand {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          border-radius: 6px;
        }

        .ab-header__brand:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 4px;
        }

        .ab-header__brand img {
          height: 30px;
          width: auto;
          display: block;
          transition: height 0.25s var(--ab-ease);
        }

        .ab-header.is-compact .ab-header__brand img { height: 26px; }

        .ab-header__nav {
          display: flex;
          align-items: center;
          gap: 1.75rem;
          margin-right: auto;
        }

        .ab-header__link {
          position: relative;
          background: none;
          border: 0;
          padding: 0.375rem 0;
          font-family: var(--ab-font-body);
          font-size: 0.9375rem;
          font-weight: 500;
          color: var(--ab-ink-2);
          cursor: pointer;
          transition: color 0.2s var(--ab-ease);
        }

        .ab-header__link::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          height: 2px;
          width: 100%;
          background: var(--ab-blue);
          border-radius: 2px;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.25s var(--ab-ease);
        }

        .ab-header__link:hover { color: var(--ab-ink); }
        .ab-header__link:hover::after { transform: scaleX(1); }

        .ab-header__link:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 4px;
          border-radius: 4px;
        }

        .ab-header__actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        }

        .ab-header__login { position: relative; }

        .ab-header__loginBtn {
          display: inline-flex;
          align-items: center;
          gap: 0.3125rem;
          background: none;
          border: 0;
          padding: 0.5rem 0.25rem;
          font-family: var(--ab-font-body);
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--ab-ink-2);
          cursor: pointer;
          transition: color 0.2s var(--ab-ease);
        }

        .ab-header__loginBtn:hover { color: var(--ab-blue); }

        .ab-header__loginBtn:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 3px;
          border-radius: 6px;
        }

        .ab-header__loginBtn svg { transition: transform 0.25s var(--ab-ease); }
        .ab-header__loginBtn svg.is-open { transform: rotate(180deg); }

        .ab-header__menu {
          position: absolute;
          top: calc(100% + 0.625rem);
          right: 0;
          min-width: 262px;
          padding: 0.375rem;
          background: var(--ab-paper);
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-inner);
          box-shadow: var(--ab-shadow-md);
          animation: abMenuIn 0.18s var(--ab-ease);
        }

        @keyframes abMenuIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: none; }
        }

        .ab-header__menuItem {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.6875rem 0.75rem;
          border-radius: 9px;
          color: var(--ab-ink-2);
          text-decoration: none;
          transition: background-color 0.18s var(--ab-ease), color 0.18s var(--ab-ease);
        }

        .ab-header__menuItem:hover { background: var(--ab-blue-50); color: var(--ab-blue); }

        .ab-header__menuItem:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: -2px;
        }

        .ab-header__menuItem strong {
          display: block;
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--ab-ink);
        }

        .ab-header__menuItem em {
          display: block;
          font-style: normal;
          font-size: 0.8125rem;
          color: var(--ab-ink-3);
          margin-top: 1px;
        }

        .ab-header__burger {
          display: none;
          margin-left: auto;
          width: 42px;
          height: 42px;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-inner);
          background: var(--ab-paper);
          color: var(--ab-ink);
          cursor: pointer;
        }

        .ab-header__burger:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 2px;
        }

        .ab-drawer {
          position: fixed;
          inset: 62px 0 0;
          z-index: var(--ab-z-drawer);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 2rem;
          padding: 1.5rem var(--ab-gutter) 2.5rem;
          background: var(--ab-paper);
          overflow-y: auto;
          animation: abDrawerIn 0.22s var(--ab-ease);
        }

        @keyframes abDrawerIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: none; }
        }

        .ab-drawer__nav { display: flex; flex-direction: column; }

        .ab-drawer__link {
          background: none;
          border: 0;
          border-bottom: 1px solid var(--ab-line);
          padding: 1.0625rem 0;
          text-align: left;
          font-family: var(--ab-font-display);
          font-size: 1.375rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--ab-ink);
          cursor: pointer;
        }

        .ab-drawer__link:active { color: var(--ab-blue); }

        .ab-drawer__foot { display: flex; flex-direction: column; gap: 0.875rem; }

        .ab-drawer__legend {
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--ab-ink-3);
          margin-top: 0.5rem;
        }

        .ab-drawer__apps { display: flex; flex-wrap: wrap; gap: 0.5rem; }

        .ab-drawer__app {
          display: inline-flex;
          align-items: center;
          gap: 0.4375rem;
          padding: 0.5625rem 0.875rem;
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-pill);
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--ab-ink-2);
          text-decoration: none;
        }

        .ab-drawer__app:active { border-color: var(--ab-blue); color: var(--ab-blue); }

        @media (max-width: 940px) {
          .ab-header__nav,
          .ab-header__actions { display: none; }
          .ab-header__burger { display: inline-flex; }
          .ab-header__inner,
          .ab-header.is-compact .ab-header__inner { height: 62px; }
        }
      `}</style>
    </>
  );
};

export default Header;
